export function getTransportPreferenceEmoji(pref: string | undefined | null): string {
	const p = (pref || '').toLowerCase();
	if (p.includes('train')) return '🚆';
	if (p.includes('bicycle') || p.includes('bike') || p.includes('cycling')) return '🚲';
	if (p.includes('car')) return '🚗';
	if (p.includes('camper')) return '🚐';
	if (p.includes('motorcycle') || p.includes('motorbike') || p.includes('scooter')) return '🏍️';
	return '🧭';
}

export function getTransportEmoji(mode?: string | null): string {
	const m = (mode || '').toLowerCase();
	if (m.includes('car') || m.includes('driving') || m.includes('automobile') || m.includes('vehicle')) return '🚗';
	if (m.includes('motorcycle') || m.includes('motorbike') || m.includes('scooter')) return '🏍️';
	if (m.includes('walk') || m.includes('foot') || m.includes('hiking') || m.includes('trekking')) return '🚶';
	if (m.includes('van') || m.includes('camper')) return '🚐';
	if (m.includes('plane') || m.includes('flight') || m.includes('air')) return '✈️';
	if (m.includes('bike') || m.includes('bicycle') || m.includes('cycling')) return '🚲';
	if (m.includes('train') || m.includes('rail')) return '🚆';
	if (m.includes('bus')) return '🚌';
	if (m.includes('boat') || m.includes('ship') || m.includes('ferry') || m.includes('vessel')) return '⛴️';
	return ' ';
}

/**
 * Night train: a train the traveller sleeps on (backend mode `night_train`,
 * docs/product/night-trains/README.md). It keeps the train emoji, so lists of
 * mode emojis show one train; the moon marks the night wherever it is named.
 */
export function isNightTrainMode(mode?: string | null): boolean {
	const m = (mode || '').toLowerCase();
	return m.includes('night') && m.includes('train');
}

/** Mode emojis without repeats (a train and a night train are both 🚆). */
export function uniqueModeEmojis(modes: Array<string | null | undefined>): Array<{ emoji: string; label: string }> {
	const seen = new Map<string, string>();
	for (const m of modes) {
		const emoji = getTransportEmoji(m);
		if (!emoji.trim()) continue;
		const label = getModeLabel(m);
		seen.set(emoji, seen.has(emoji) ? `${seen.get(emoji)}, ${label}` : label);
	}
	return Array.from(seen, ([emoji, label]) => ({ emoji, label }));
}

export function getModeLabel(mode?: string | null): string {
	const m = (mode || '').toLowerCase();
	// Before the generic 'train' branch below.
	if (isNightTrainMode(m)) return 'Night train';
	// Must be checked before the generic 'car' branch below, or car_sharing
	// collapses into the same "Car" label (duplicate rows in the compare dialog).
	if (m.includes('car_sharing') || m.includes('car sharing') || m.includes('carsharing')) return 'Car Sharing';
	if (m.includes('car') || m.includes('automobile') || m.includes('vehicle')) return 'Car';
	if (m.includes('motorcycle') || m.includes('motorbike') || m.includes('scooter')) return 'Motorcycle';
	if (m.includes('walk') || m.includes('foot') || m.includes('hiking') || m.includes('trekking')) return 'Walking';
	if (m.includes('van') || m.includes('camper')) return 'Camper van';
	if (m.includes('plane') || m.includes('flight') || m.includes('air')) return 'Airplane';
	if (m.includes('bike') || m.includes('bicycle') || m.includes('cycling')) return 'Bicycle';
	if (m.includes('train') || m.includes('rail')) return 'Train';
	if (m.includes('bus')) return 'Bus';
	if (m.includes('boat') || m.includes('ship') || m.includes('ferry') || m.includes('vessel')) return 'Boat';
	return 'Unknown';
}

/**
 * Transport modes offered in the leg mode dropdown. Single source of truth,
 * reused by the legacy Journey editor and the inline route editor so the list
 * (and ordering) stays consistent.
 */
export const TRANSPORT_MODE_OPTIONS: Array<{ value: string; label: string }> = [
	{ value: 'train', label: 'Train 🚆' },
	// Always listed. Where the leg cannot take one, the pickers show it
	// disabled with its reason (features/my-trips/nightTrain.ts, modeOptionsForLeg).
	{ value: 'night_train', label: 'Night train 🌙' },
	{ value: 'bus', label: 'Bus 🚌' },
	{ value: 'airplane', label: 'Airplane ✈️' },
	{ value: 'car_sharing', label: 'Car Sharing 🚗' },
	{ value: 'boat', label: 'Boat ⛴️' },
	{ value: 'car', label: 'Car 🚗' },
	{ value: 'motorcycle', label: 'Motorcycle 🏍️' },
	{ value: 'bicycle', label: 'Bicycle 🚴' },
	{ value: 'auto', label: 'Let Jody decide' },
];

/**
 * Whether the mode is a concrete, recognized transport mode. False for
 * 'auto'/'unknown'/empty — surfaces should then show NO emissions at all
 * (getEmissionLevel would misleadingly default to 'low').
 */
export function isKnownTransportMode(mode?: string | null): boolean {
	return getModeLabel(mode) !== 'Unknown';
}

/** Airplane detection across the stored mode vocabulary ('airplane', 'flight', 'plane'…). */
export function isAirplaneMode(mode?: string | null): boolean {
	return getModeLabel(mode) === 'Airplane';
}

/**
 * Why a flight's displayed time exceeds the flying time (the fixed ground
 * overhead, backend legComputation.FLIGHT_OVERHEAD_MINUTES: 2 h 30 since
 * 2026-09-29, 2 h before. The number below must say the same). One string shared
 * by every surface that shows a flight time estimate — leg sheet Details row
 * and the journey timeline leg cards — so the story can't drift between them.
 */
export const FLIGHT_TIME_TOOLTIP = 'Includes 2 hours and 30 minutes of airport time: getting to and from the airports, checking in, security, waiting and boarding. Only the rest is spent flying.';

/**
 * Qualitative emission level for a transport mode (prototype `.emi` low/med/high,
 * plus "very low"): night train, bicycle and walking = very low, rail/bus =
 * low, car/motorcycle/van/ferry = medium, flights = high. Used to color the
 * leg's "Very low / Low / Medium / High emissions" label and the route map's
 * segments.
 */
export type EmissionLevel = 'vlow' | 'low' | 'med' | 'high';

export function getEmissionLevel(mode?: string | null): EmissionLevel {
	const m = (mode || '').toLowerCase();
	// A night train is a train that also saves the hotel night it replaces
	// (Paulo 2026-09-30): the lowest band, on the map and on the labels.
	if (isNightTrainMode(m)) return 'vlow';
	if (m.includes('plane') || m.includes('flight') || m.includes('air')) return 'high';
	if (m.includes('car') || m.includes('motorcycle') || m.includes('motorbike') || m.includes('scooter')
		|| m.includes('van') || m.includes('camper') || m.includes('ferry') || m.includes('boat') || m.includes('ship')) return 'med';
	// Bicycle and walking: very low too (Paulo 2026-09-30), though the map's
	// legend does not list them (features/maps/carbonBands.ts).
	if (m.includes('bike') || m.includes('bicycle') || m.includes('cycling')
		|| m.includes('walk') || m.includes('foot') || m.includes('hiking') || m.includes('trekking')) return 'vlow';
	return 'low'; // train, bus, and default
}

/** Color per emission level: the leg label, compare dialog and route map
 *  (features/maps/carbonBands.ts) all read this so they can't drift apart. */
export const EMISSION_LEVEL_COLOR: Record<EmissionLevel, string> = {
	// Readable on white; the route map draws 'vlow' in its neon green
	// (features/maps/carbonBands.ts), too bright for text.
	vlow: '#0e9f5a',
	low: '#1f9b6e',
	med: '#c2912f',
	high: '#cf5a3c',
};

/** The word for each level, wherever a leg's emissions are labelled. */
export const EMISSION_LEVEL_LABEL: Record<EmissionLevel, string> = {
	vlow: 'Very low',
	low: 'Low',
	med: 'Medium',
	high: 'High',
};

/** Why a night train is "very low", said next to the label. */
export const NIGHT_TRAIN_EMISSIONS_NOTE = 'A night train also saves the emissions of the hotel night it replaces.';

export function summarizeTransportModes(journeyData: any): Array<{ emoji: string; label: string }>{
	if (!journeyData || !journeyData.legs || !Array.isArray(journeyData.legs)) return [];

	// Only handle v2 format (legs array with transportMode)
	const seen = new Set<string>();
	for (const leg of journeyData.legs) {
		const label = getModeLabel(leg?.transportMode);
		seen.add(label);
	}
	return Array.from(seen).map((label) => ({ label, emoji: getTransportEmoji(label) }));
} 